import { useEffect, useRef, useState, type ReactNode, type RefObject, type TouchEvent as ReactTouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from './useMediaQuery';
import { useClickOutside, FLOW_PORTAL_PROPS } from './useClickOutside';
import { useAnchoredPosition } from './contact/useAnchoredPosition';
import { pushEscapeLayer } from '../ui/escape-stack';

/**
 * ResponsivePopover — one container, two renderings, same children, per the
 * prototype's behavioral contract (`docs/flow/custom-views-prototype.html`,
 * `.pop` + its `@media (max-width:720px)` block): an anchored popover on
 * desktop, a viewport-FIXED bottom sheet under 720px. Both modes portal to
 * `document.body` so they escape any `overflow-hidden`/`overflow-y-auto`
 * ancestor (same reasoning as `useAnchoredPosition`'s doc comment).
 *
 * Desktop: positioned via `useAnchoredPosition` against `anchorRef` (flip/
 * clamp geometry, internal-scroll size cap). The outer panel imposes no
 * height cap of its own — content that needs a scroll box (e.g.
 * `ColumnsPopover`'s column list) sizes itself, matching the prototype's
 * `.pop` (unbounded) vs `.colpanel` (its own `max-height`/`overflow`).
 *
 * Mobile (`< 720px`, via `useMediaQuery('(max-width: 719px)')`): full-width,
 * fixed to the viewport bottom, `max-h-[80vh]` with the OUTER panel scrolling
 * (matching the prototype's mobile `.pop` override), rounded top corners, a
 * grab handle, and drag-to-dismiss on the handle (interaction precedent:
 * `components/skills/LibraryBottomSheet.tsx` — that one is pane-absolute,
 * this one is viewport-fixed via `position: fixed`).
 *
 * Dismissal is `useClickOutside` (mousedown outside the panel) plus Escape,
 * in both modes — no separate close affordance is required by the contract.
 * The panel root itself carries `FLOW_PORTAL_PROPS` so a nested picker
 * portal (e.g. `FieldPicker`'s menu, also `document.body`-portaled) doesn't
 * treat THIS panel as outside either — see `useClickOutside`'s doc comment
 * for the full nested-portal rationale.
 *
 * ── `withBackdrop` (bug fix, Spencer report 2026-08-13) ───────────────────
 * Opt-in, off by default. Without it, this popover has no backdrop — an
 * outside click is a REAL click on whatever page content is actually there,
 * so dismissing and activating something behind the popover happened in one
 * gesture (reported for the Filters popover: dismissing it also toggled an
 * unrelated sidebar control). `useClickOutside`'s own click-cancellation
 * (`outside-click-stack.ts`) only ever intercepts events through THIS
 * module's own dispatch path — it cannot guard against some OTHER,
 * independent listener elsewhere in the app also reacting to the same
 * native click. `withBackdrop` renders an invisible `fixed inset-0` div at
 * a z-index BELOW the panel/nested-picker z-index (60, `useAnchoredPosition`)
 * but above ordinary page content — the browser's own native hit-testing
 * then makes THIS div the click's real target for anything outside the
 * panel, so nothing behind it (any independent listener, any other
 * element's own onClick) ever sees the event at all, regardless of listener
 * registration order or timing. The backdrop carries NO click handler of
 * its own — `useClickOutside`'s existing document-level listener still
 * decides whether to close (respecting the outside-click-stack's "only the
 * topmost layer closes" rule when a nested picker is open on top), exactly
 * as it did before; the backdrop only changes WHERE the native click lands,
 * not who decides what happens next. Scoped to the Filters popover only
 * (`FlowTable.tsx`) rather than every `ResponsivePopover` consumer: enabling
 * it everywhere would also block the existing one-click switch between
 * sibling toolbar popovers (Filters ↔ Columns ↔ Group) for surfaces that
 * never reported this bug.
 */

/** Fraction of sheet height past which a downward drag closes the sheet. */
const CLOSE_DRAG_RATIO = 0.3;

export interface ResponsivePopoverProps {
  open: boolean;
  onClose: () => void;
  /** Trigger element the desktop popover anchors to. Ignored in sheet mode. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Accessible name for the dialog region. */
  ariaLabel: string;
  /** Same children rendered in both modes. */
  children: ReactNode;
  /** Extra classes for the panel (e.g. a width hint for the desktop popover). */
  className?: string;
  /** See this module's doc comment — off by default, opt in per consumer. */
  withBackdrop?: boolean;
}

export function ResponsivePopover({
  open, onClose, anchorRef, ariaLabel, children, className, withBackdrop,
}: ResponsivePopoverProps) {
  const isNarrow = useMediaQuery('(max-width: 719px)');
  const panelRef = useClickOutside(open, onClose, anchorRef);
  const anchoredStyle = useAnchoredPosition(anchorRef, panelRef, open && !isNarrow, onClose);

  const dragStartYRef = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  // Close on Escape, either mode — R2: registers on the shared escape stack
  // (`escape-stack.ts`) rather than its own raw `document` listener. When
  // this is the only open layered surface (the overwhelming majority of
  // callers — every standalone popover in the app), it's also the top of
  // the stack, so behavior is unchanged: Escape closes it. The stack only
  // changes anything when a SECOND layer (e.g. this popover nested inside a
  // `Drawer`, as `ViewNameDialog` is inside `BrowseAllModal`) is also open —
  // then only the topmost layer's Escape fires, not both.
  useEffect(() => {
    if (!open) return;
    return pushEscapeLayer(onClose);
  }, [open, onClose]);

  // Reset drag state whenever the sheet closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setDragOffset(0);
      dragStartYRef.current = null;
    }
  }, [open]);

  const handleTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    dragStartYRef.current = e.touches[0]?.clientY ?? null;
  };
  const handleTouchMove = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (dragStartYRef.current === null) return;
    const deltaY = (e.touches[0]?.clientY ?? dragStartYRef.current) - dragStartYRef.current;
    if (deltaY > 0) setDragOffset(deltaY);
  };
  const handleTouchEnd = () => {
    const sheet = panelRef.current;
    const height = sheet?.getBoundingClientRect().height ?? 0;
    if (height > 0 && dragOffset > height * CLOSE_DRAG_RATIO) onClose();
    setDragOffset(0);
    dragStartYRef.current = null;
  };

  if (!open) return null;

  // No click handler of its own — see this module's "withBackdrop" doc
  // comment. Purely a native-hit-testing blocker at a z-index BELOW the
  // panel/nested-picker z-index (50 in sheet mode, 60 anchored) so an
  // outside click's real target is this div, not whatever page content is
  // actually behind it; `useClickOutside`'s existing listener still owns
  // the close decision.
  const backdrop = withBackdrop && (
    <div data-testid="responsive-popover-backdrop" aria-hidden="true" className="fixed inset-0 z-40" />
  );

  if (isNarrow) {
    const transform = `translateY(${dragOffset}px)`;
    const transition = dragStartYRef.current !== null ? 'none' : 'transform 200ms ease';
    return createPortal(
      <>
        {backdrop}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          data-testid="responsive-popover-sheet"
          {...FLOW_PORTAL_PROPS}
          className={`fixed inset-x-0 bottom-0 z-50 flex max-h-[80vh] w-full flex-col overflow-y-auto rounded-t-2xl border-t border-secondary bg-white shadow-xl dark:border-nightshade-700 dark:bg-nightshade-800 ${className ?? ''}`}
          style={{ transform, transition }}
        >
          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            className="flex flex-shrink-0 cursor-grab justify-center touch-none pb-1 pt-2"
            aria-hidden="true"
          >
            <div className="h-1 w-9 rounded-full bg-nightshade-300 dark:bg-nightshade-600" />
          </div>
          {children}
        </div>
      </>,
      document.body,
    );
  }

  return createPortal(
    <>
      {backdrop}
      <div
        ref={panelRef}
        role="dialog"
        aria-label={ariaLabel}
        data-testid="responsive-popover-anchored"
        {...FLOW_PORTAL_PROPS}
        className={`rounded-md border border-secondary bg-white shadow-popover dark:border-nightshade-700 dark:bg-nightshade-800 ${className ?? ''}`}
        style={anchoredStyle}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
